import {
  ApolloClient,
  split,
  ApolloLink,
  InMemoryCache,
} from '@apollo/client'
import { isNumber } from 'lodash'
import { getMainDefinition } from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import { createClient } from 'graphql-ws';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import {
  GQL_API_URL,
  GQL_CLIENT_TIMEOUT,
  APP_GQL_WS_URL,
} from '../constants/env'
import {
  emitSessionLost,
  fetchWsTicket,
  getLegacyReportToken,
  isUserAuthenticated,
} from '../helpers/session'

const timeoutLink = new ApolloLinkTimeout(GQL_CLIENT_TIMEOUT)

const legacyReportToken = getLegacyReportToken()

const fileUploadLink = createUploadLink({
  uri: legacyReportToken ? GQL_API_URL : '/api/graphql',
  credentials: 'same-origin',
  headers: {
    'apollo-require-preflight': 'true',
    ...(legacyReportToken ? { 'x-auth-token': legacyReportToken } : {}),
  },
})

const wsLink = new GraphQLWsLink(createClient({
  url: APP_GQL_WS_URL,
  lazy: true,
  keepAlive: 1000 * 2,
  retryAttempts: 10000000,
  shouldRetry: () => Boolean(legacyReportToken) || isUserAuthenticated(),
  connectionParams: async () => {
    if (legacyReportToken) return { authToken: legacyReportToken }
    return { ticket: await fetchWsTicket() }
  }
}));

function incrementRetryAttemptContext(operation) {
  let _prevRetryAttempt = isNumber(operation.getContext()?.retryAttempt) ? operation.getContext()?.retryAttempt : 0
  _prevRetryAttempt++
  operation.setContext({
    retryAttempt: _prevRetryAttempt,
  })
  return operation
}

const SESSION_LOST_PATTERN = /^(UNAUTHORIZED|Session expired)$/

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (networkError) {
    incrementRetryAttemptContext(operation)
    if (networkError.statusCode === 401) {
      emitSessionLost()
      return
    }
    try {
      JSON.parse(networkError.bodyText)
    } catch (error) {
      networkError.message = networkError.bodyText
    }
    return forward(operation)
  }

  if (graphQLErrors) {
    incrementRetryAttemptContext(operation)
    let sessionLost = false
    graphQLErrors.forEach(({ message, path }) => {
      console.log(`GraphQL Error: ${message}`, path && path.join('/'))
      if (SESSION_LOST_PATTERN.test(message)) sessionLost = true
    })
    if (sessionLost && !legacyReportToken) {
      emitSessionLost()
      return
    }
    return forward(operation)
  }
})

const retrylink = new RetryLink({
  delay: {
    initial: 300,
    max: 5000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      if (error?.message) {
        if (error?.message === 'Failed to fetch') {
          return true
        }
        if (/Knex: Timeout acquiring a connection/i.test(error?.message)) {
          return true
        }
      }
      return false
    },
  },
})

const httpLinks = ApolloLink.from([errorLink, retrylink, timeoutLink, fileUploadLink])

const splittedLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  httpLinks,
)

const apolloClient = new ApolloClient({
  link: splittedLink,
  cache: new InMemoryCache({}),
  credentials: 'same-origin',
})

export default apolloClient
